import { useState } from 'react';
import { Button, Card, Input } from '@novaquantis/design-system';
import { useChangePasswordMutation } from '../../queries/auth';

const ChangePassword = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const changePasswordMutation = useChangePasswordMutation();
  const passwordLoading = changePasswordMutation.isPending;
  const [validationErrors, setValidationErrors] = useState<{
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const validatePasswordSecurity = (password: string): { valid: boolean; error?: string } => {
    if (password.length < 6) {
      return { valid: false, error: 'Password must be at least 6 characters' };
    }

    if (password.length > 128) {
      return { valid: false, error: 'Password must be less than 128 characters' };
    }

    const sqlInjectionPatterns = [
      // oxlint-disable-next-line no-useless-escape -- escapes kept for readability of the encoded/literal pairs
      /(\%27)|(\')|(\-\-)|(\%23)|(#)/i,
      // oxlint-disable-next-line no-useless-escape -- escapes kept for readability of the encoded/literal pairs
      /((\%3D)|(=))[^\n]*((\%27)|(\')|(\-\-)|(\%3B)|(;))/i,
      // oxlint-disable-next-line no-useless-escape -- escapes kept for readability of the encoded/literal pairs
      /\w*((\%27)|(\'))((\%6F)|o|(\%4F))((\%72)|r|(\%52))/i,
      // oxlint-disable-next-line no-useless-escape -- escapes kept for readability of the encoded/literal pairs
      /((\%27)|(\'))union/i,
    ];

    for (const pattern of sqlInjectionPatterns) {
      if (pattern.test(password)) {
        return { valid: false, error: 'Password contains invalid characters' };
      }
    }

    const scriptInjectionPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
    ];

    for (const pattern of scriptInjectionPatterns) {
      if (pattern.test(password)) {
        return { valid: false, error: 'Password contains invalid characters' };
      }
    }

    if (password.includes('\0')) {
      return { valid: false, error: 'Password contains invalid characters' };
    }

    return { valid: true };
  };

  const validateForm = (): boolean => {
    const errors: {
      oldPassword?: string;
      newPassword?: string;
      confirmPassword?: string;
    } = {};

    if (!oldPassword) {
      errors.oldPassword = 'Current password is required';
    }

    if (!newPassword) {
      errors.newPassword = 'New password is required';
    } else {
      const passwordValidation = validatePasswordSecurity(newPassword);
      if (!passwordValidation.valid) {
        errors.newPassword = passwordValidation.error;
      }
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (oldPassword && newPassword && oldPassword === newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);
    setValidationErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      const { data } = await changePasswordMutation.mutateAsync({ oldPassword, newPassword, confirmPassword });
      setPasswordSuccess(data.message || 'Password changed successfully');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setPasswordError(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
          'Failed to change password',
      );
    }
  };

  return (
    <div className="w-full max-w-[960px]">
      <Card className="flex flex-col gap-8">
        <h2 className="m-0 font-display text-xl font-bold text-foreground">Settings</h2>
        <h3 className="m-0 text-base font-semibold text-secondary">Security</h3>

        <form
          className="flex max-w-[min(28rem,50%)] flex-col gap-2 [&>div]:!mb-4 [&>div>input]:!border [&>div>input]:!border-[color:var(--border-slate)] [&>div>input]:!bg-[color:var(--nq-surface-raised)] [&>div>input]:!px-3 [&>div>input]:!py-[11px] [&>div>input]:!text-sm [&>div>input]:!text-[color:var(--text-primary)] [&>div>input:hover]:!border-white/[0.18] [&>div>input:hover]:!bg-white/[0.06] [&>div>label]:!text-sm [&>div>label]:!text-[color:var(--text-secondary)] max-md:max-w-full"
          onSubmit={handleSubmit}
          autoComplete="on"
        >
          <Input
            label="Current password"
            id="oldPassword"
            name="current-password"
            type="password"
            value={oldPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOldPassword(e.target.value);
              if (validationErrors.oldPassword) {
                setValidationErrors((prev) => ({ ...prev, oldPassword: undefined }));
              }
            }}
            autoComplete="current-password"
            placeholder="Enter your current password"
            required
            disabled={passwordLoading}
            variant="onDark"
          />
          {validationErrors.oldPassword && (
            <span className="-mt-2 mb-4 block text-xs text-[color:var(--nq-down-soft)]">{validationErrors.oldPassword}</span>
          )}

          <Input
            label="New password"
            id="newPassword"
            name="new-password"
            type="password"
            value={newPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setNewPassword(e.target.value);
              if (validationErrors.newPassword) {
                setValidationErrors((prev) => ({ ...prev, newPassword: undefined }));
              }
            }}
            autoComplete="new-password"
            placeholder="Enter your new password"
            required
            minLength={6}
            maxLength={128}
            disabled={passwordLoading}
            variant="onDark"
          />
          {validationErrors.newPassword && (
            <span className="-mt-2 mb-4 block text-xs text-[color:var(--nq-down-soft)]">{validationErrors.newPassword}</span>
          )}

          <Input
            label="Confirm new password"
            id="confirmPassword"
            name="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setConfirmPassword(e.target.value);
              if (validationErrors.confirmPassword) {
                setValidationErrors((prev) => ({ ...prev, confirmPassword: undefined }));
              }
            }}
            autoComplete="new-password"
            placeholder="Confirm your new password"
            required
            minLength={6}
            maxLength={128}
            disabled={passwordLoading}
            variant="onDark"
          />
          {validationErrors.confirmPassword && (
            <span className="-mt-2 mb-4 block text-xs text-[color:var(--nq-down-soft)]">{validationErrors.confirmPassword}</span>
          )}

          {passwordError && (
            <div className="rounded-[var(--radius-md)] border border-negative/35 bg-negative/15 px-8 py-6 text-sm text-[color:var(--nq-down-soft)]">
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="rounded-[var(--radius-md)] border border-positive/35 bg-positive/12 px-8 py-6 text-sm text-[color:var(--nq-up-bright)]">
              {passwordSuccess}
            </div>
          )}

          <Button type="submit" disabled={passwordLoading} variant="glow" fullWidth>
            {passwordLoading ? 'Changing password…' : 'Change password'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ChangePassword;
